import { computed } from 'vue';

import { EVatRate } from '@/domains/getVatRateOptions';
import { ETenantPriceViewMode } from '@/contexts/accountingContext/domain/constants';
import { showErrorNotification } from '@/domains/showErrorNotification';
import { useWalletStore } from '@/stores/wallet';
import { useTenantsStore } from '@/stores/tenants';
import { canBrowseWalletsByRole } from '@/domains/permissions/role/wallets';
import { hasBaseCarrierWorkplaceSubscription, hasCarrierWorkplaceSubscription } from '@/domains/permissions/subscription';
import { useNotificationStore } from '@/stores/notifications';

const useTenantServices = () => {
  const tenantsStore = useTenantsStore();
  const walletStore = useWalletStore();
  const notificationStore = useNotificationStore();
  const priceViewMode = computed(() => tenantsStore.currentTenantDetails?.legalEntity?.priceViewMode);
  const userTenants = computed(() => tenantsStore.userTenants || []);

  const getHasOpenClosedBiddingAdditionalSubscription = computed(
    () => tenantsStore.getHasOpenClosedBiddingAdditionalSubscription);

  const canFetchWalletInfo = computed(() => canBrowseWalletsByRole()
    && (hasCarrierWorkplaceSubscription() || hasBaseCarrierWorkplaceSubscription())
    && getHasOpenClosedBiddingAdditionalSubscription.value,
  );

  const fetchCommonTenantData = (withInfoRequest: boolean = false) => {
    if (!withInfoRequest) {
      const promises: Promise<void>[] = [notificationStore.loadUnreadNotificationsCount()];

      if (userTenants.value?.length) {
        promises.push(tenantsStore.loadTenantReferenceData());
      }

      if (canFetchWalletInfo.value) {
        promises.push(walletStore.loadWallet());
      }

      return Promise.all(promises)
        .catch(showErrorNotification);
    }

    return tenantsStore.loadTenantInfo()
      .then(() => {
        const promises: Promise<void>[] = [notificationStore.loadUnreadNotificationsCount()];

        if (userTenants.value?.length) {
          promises.push(tenantsStore.loadTenantReferenceData());
        }

        if (canFetchWalletInfo.value) {
          promises.push(walletStore.loadWallet());
        }

        Promise.all(promises)
          .catch(showErrorNotification);
      })
      .catch(showErrorNotification);
  };

  const updateTenantData = async () => {
    if (!tenantsStore.userTenants?.length) return;
    await tenantsStore.loadTenantInfo();
    await fetchCommonTenantData();
  };

  const vatRateByTenantSettings = computed(
    () => (priceViewMode.value !== ETenantPriceViewMode.without_vat ? EVatRate.vat_20 : EVatRate.vat_none));

  return {
    vatRateByTenantSettings,

    updateTenantData,
  };
};

export default useTenantServices;
